import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { Language, MenuItem } from '../types';
import { useMenu } from '../src/contexts/MenuContext';
import { useRecentlyViewed } from '../src/utils/useRecentlyViewed';
import { useSettings } from '../src/contexts/SettingsContext';
import { DEFAULT_MENU_ITEM_IMAGE } from '../src/constants';
import { t } from '../translations';

interface Props {
  language: Language;
  onSelect: (item: MenuItem) => void;
}

/**
 * Карусель последних просмотренных блюд. Показывается только если ids.length ≥ 2 —
 * с одним блюдом карусель не имеет смысла. Скрыта на /takeaway или при активном
 * поиске (см. логику в Home).
 */
const RecentlyViewed: React.FC<Props> = ({ language, onSelect }) => {
  const { ids } = useRecentlyViewed();
  const { menuData } = useMenu();
  const { settings } = useSettings();

  const items = useMemo(() => {
    const all = menuData.flatMap((c) => c.items);
    return ids.map((id) => all.find((it) => it.id === id)).filter((it): it is MenuItem => !!it);
  }, [ids, menuData]);

  if (items.length < 2) return null;

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2 px-1">
        <Clock className="w-3.5 h-3.5 text-[var(--text-secondary)]" />
        <h3 className="text-xs font-black uppercase tracking-widest text-[var(--text-secondary)]">
          {t('home.recentlyViewed', language)}
        </h3>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 pb-1">
        {items.map((item) => (
          <motion.button
            key={item.id}
            whileTap={{ scale: 0.95 }}
            onClick={() => onSelect(item)}
            className="flex-shrink-0 w-32 bg-[var(--bg-card)] rounded-2xl border border-[var(--glass-border)] overflow-hidden shadow-sm active:shadow-md transition-shadow text-left"
          >
            <div className="w-full h-20 bg-[var(--neutral-bg)] overflow-hidden">
              <img
                src={item.image || DEFAULT_MENU_ITEM_IMAGE}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_MENU_ITEM_IMAGE; }}
              />
            </div>
            <div className="px-2 py-2">
              <p className="text-xs font-bold text-[var(--text-primary)] line-clamp-1">
                {item.names[language] || item.names.ru}
              </p>
              <p className="text-[10px] font-bold text-[var(--accent-color)] mt-0.5">
                {item.price} {settings.currency}
              </p>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default RecentlyViewed;
